<template>
  <div class="pc-page">
    <Navigation />

    <div class="container py-4">
      <div class="d-flex align-items-center flex-wrap gap-2 mb-4">
        <h3 class="mb-0 d-flex align-items-center">
          <i class="mdi mdi-shield-crown text-warning me-2"></i>{{ brand.name }} Platform Console
        </h3>
        <span class="pc-system-owner-pill" title="You are viewing the platform-level (multi-tenant) console">
          <i class="mdi mdi-crown me-1"></i>SYSTEM OWNER
        </span>
        <button class="btn btn-sm btn-success ms-auto" @click="openNewOrg">
          <i class="mdi mdi-hospital-building me-1"></i> New Organisation
        </button>
        <button class="btn btn-sm btn-outline-secondary" @click="refreshAll">
          <i class="mdi mdi-refresh me-1"></i> Refresh
        </button>
      </div>

      <!-- ============ NEW ORGANISATION MODAL ============ -->
      <div v-if="newOrg.show" class="pc-modal-bg" @click.self="closeNewOrg">
        <div class="pc-modal">
          <div class="pc-modal-head">
            <strong><i class="mdi mdi-hospital-building me-2"></i>Onboard a new organisation</strong>
            <button class="btn-close" @click="closeNewOrg"></button>
          </div>
          <div class="pc-modal-body">
            <div v-if="newOrg.error" class="alert alert-danger py-2 mb-3">{{ newOrg.error }}</div>
            <p class="text-muted small mb-3">
              Manually onboard a hospital / clinic that hasn't self-registered. It goes in as <strong>Active</strong> immediately &mdash;
              no email verification needed. The admin gets a login email with the credentials you set below.
            </p>

            <div class="pc-modal-section-h">HOSPITAL</div>
            <div class="row g-2 mb-3">
              <div class="col-md-8"><label class="small">Hospital / clinic name *</label>
                <input v-model="newOrg.form.hospital_name" class="form-control form-control-sm" placeholder="e.g. Nairobi Wellness Clinic" />
              </div>
              <div class="col-md-4"><label class="small">City / town</label>
                <input v-model="newOrg.form.city" class="form-control form-control-sm" placeholder="Nairobi" />
              </div>
              <div class="col-md-6"><label class="small">Hospital email</label>
                <input v-model="newOrg.form.hospital_email" type="email" class="form-control form-control-sm" placeholder="admin@facility.co.ke" />
              </div>
              <div class="col-md-6"><label class="small">Hospital phone</label>
                <input v-model="newOrg.form.hospital_phone" class="form-control form-control-sm" placeholder="0712345678" />
              </div>
            </div>

            <div class="pc-modal-section-h">FIRST ADMIN (super_admin login)</div>
            <div class="row g-2">
              <div class="col-md-6"><label class="small">First name *</label>
                <input v-model="newOrg.form.first_name" class="form-control form-control-sm" />
              </div>
              <div class="col-md-6"><label class="small">Last name *</label>
                <input v-model="newOrg.form.last_name" class="form-control form-control-sm" />
              </div>
              <div class="col-md-6"><label class="small">Username *</label>
                <input v-model="newOrg.form.username" class="form-control form-control-sm" placeholder="letters, numbers, dots, underscores" />
              </div>
              <div class="col-md-6"><label class="small">Email *</label>
                <input v-model="newOrg.form.email" type="email" class="form-control form-control-sm" placeholder="Login credentials will be sent here" />
              </div>
              <div class="col-md-6"><label class="small">Phone</label>
                <input v-model="newOrg.form.phone" class="form-control form-control-sm" placeholder="0712345678" />
              </div>
              <div class="col-md-6"><label class="small">Temporary password *</label>
                <input v-model="newOrg.form.password" class="form-control form-control-sm" placeholder="min 8 chars" />
              </div>
            </div>
          </div>
          <div class="pc-modal-foot">
            <button class="btn btn-outline-secondary" @click="closeNewOrg" :disabled="newOrg.busy">Cancel</button>
            <button class="btn btn-success" @click="submitNewOrg" :disabled="newOrg.busy">
              <span v-if="newOrg.busy"><span class="spinner-border spinner-border-sm me-1"></span>Onboarding…</span>
              <span v-else><i class="mdi mdi-check me-1"></i>Onboard &amp; approve</span>
            </button>
          </div>
        </div>
      </div>

      <!-- KPI strip -->
      <div class="row g-3 mb-4">
        <div class="col-md-3 col-6"><div class="pc-kpi">
          <div class="pc-kpi-label">Active orgs</div>
          <div class="pc-kpi-val text-success">{{ dash.org_counts && dash.org_counts.active || 0 }}</div>
          <div class="pc-kpi-sub">{{ (dash.org_counts && dash.org_counts.pending_approval) || 0 }} pending · {{ (dash.org_counts && dash.org_counts.suspended) || 0 }} suspended</div>
        </div></div>
        <div class="col-md-3 col-6"><div class="pc-kpi">
          <div class="pc-kpi-label">Monthly recurring</div>
          <div class="pc-kpi-val">{{ fmt(dash.mrr_kes) }}</div>
          <div class="pc-kpi-sub">KES, normalised</div>
        </div></div>
        <div class="col-md-3 col-6"><div class="pc-kpi">
          <div class="pc-kpi-label">Collected (MTD)</div>
          <div class="pc-kpi-val text-info">{{ fmt(dash.collected_mtd_kes) }}</div>
          <div class="pc-kpi-sub">{{ dash.mtd_payment_count || 0 }} payments this month</div>
        </div></div>
        <div class="col-md-3 col-6"><div class="pc-kpi">
          <div class="pc-kpi-label">Overdue orgs</div>
          <div class="pc-kpi-val text-danger">{{ (dash.overdue && dash.overdue.length) || 0 }}</div>
          <div class="pc-kpi-sub">{{ (dash.upcoming_30_days && dash.upcoming_30_days.length) || 0 }} due in 30 days</div>
        </div></div>
      </div>

      <!-- Tabs -->
      <ul class="nav nav-tabs pc-tabs">
        <li class="nav-item"><a class="nav-link" :class="{active:tab==='pending'}" href="javascript:void(0)" @click="tab='pending'">
          Pending queue <span class="badge bg-warning text-dark ms-1">{{ pending.length }}</span>
        </a></li>
        <li class="nav-item"><a class="nav-link" :class="{active:tab==='all'}" href="javascript:void(0)" @click="tab='all'">All organisations</a></li>
        <li class="nav-item"><a class="nav-link" :class="{active:tab==='access'}" href="javascript:void(0)" @click="switchToAccess">
          Access requests
          <span v-if="accessRequestsPending > 0" class="badge bg-danger ms-1">{{ accessRequestsPending }}</span>
        </a></li>
        <li class="nav-item"><a class="nav-link" :class="{active:tab==='billing'}" href="javascript:void(0)" @click="tab='billing'">Billing</a></li>
        <li class="nav-item"><a class="nav-link" :class="{active:tab==='audit'}" href="javascript:void(0)" @click="tab='audit'">Audit log</a></li>
      </ul>

      <!-- ACCESS REQUESTS -->
      <div v-if="tab==='access'" class="pc-panel">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <label class="small mb-0">Status:</label>
          <select v-model="accessStatusFilter" class="form-select form-select-sm pc-filter" @change="loadAccessRequests">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
          <button class="btn btn-sm btn-outline-secondary ms-auto" @click="loadAccessRequests">
            <i class="mdi mdi-refresh me-1"></i>Refresh
          </button>
        </div>

        <div v-if="!accessRequests.length" class="text-center py-5 text-muted">
          <i class="mdi mdi-check-decagram mdi-48px text-success"></i><br>
          <span v-if="accessStatusFilter==='pending'">No pending access requests. </span>
          <span v-else>No requests match.</span>
        </div>

        <div v-for="ar in accessRequests" :key="ar.request_id" class="pc-org-card"
             :class="ar.status">
          <div class="d-flex flex-wrap align-items-start gap-3">
            <div class="pc-org-avatar"><i class="mdi mdi-shield-account"></i></div>
            <div class="flex-grow-1">
              <div class="d-flex flex-wrap align-items-center gap-2">
                <strong>{{ ar.first_name }} {{ ar.last_name }}</strong>
                <span class="text-muted small">@{{ ar.username }}</span>
                <span class="badge bg-secondary">{{ ar.requester_role_name || ar.requester_role_code }}</span>
                <span class="badge" :class="{
                  'bg-warning text-dark': ar.status === 'pending',
                  'bg-success':           ar.status === 'approved',
                  'bg-danger':            ar.status === 'rejected'
                }">{{ ar.status }}</span>
              </div>
              <div class="small text-muted mt-1">
                Wants <code>{{ ar.required_level }}</code> on
                <code>{{ ar.category_code }}</code> ({{ ar.category_name || '—' }})
                · currently <code>{{ ar.current_level || 'none' }}</code>
              </div>
              <div class="small mt-2" v-if="ar.reason">
                <i class="mdi mdi-format-quote-open"></i>
                <span class="fst-italic">{{ ar.reason }}</span>
              </div>
              <div class="small text-muted mt-1">
                <i class="mdi mdi-hospital-building me-1"></i>{{ ar.hospital_name || '—' }}
                · <i class="mdi mdi-email-outline ms-1 me-1"></i>{{ ar.email || '—' }}
                · Submitted {{ fmtDate(ar.created_at) }}
              </div>
              <div v-if="ar.status !== 'pending'" class="small text-muted mt-1">
                <i :class="ar.status==='approved' ? 'mdi mdi-check-decagram text-success me-1' : 'mdi mdi-close-octagon text-danger me-1'"></i>
                {{ ar.status }} by {{ ar.decided_first }} {{ ar.decided_last }}
                on {{ fmtDate(ar.decided_at) }}
                <span v-if="ar.granted_level"> · granted <code>{{ ar.granted_level }}</code></span>
                <div v-if="ar.decided_note" class="ms-4 fst-italic">"{{ ar.decided_note }}"</div>
              </div>
            </div>
            <div v-if="ar.status === 'pending'" class="d-flex flex-column gap-2" style="min-width:150px;">
              <select v-model="ar._grantLevel" class="form-select form-select-sm" :disabled="ar._busy">
                <option value="view">view</option>
                <option value="view_masked">view_masked</option>
                <option value="edit">edit</option>
              </select>
              <button class="btn btn-sm btn-success" @click="approveAccessRequest(ar)" :disabled="ar._busy">
                <span v-if="ar._busy" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="mdi mdi-check me-1"></i>
                {{ ar._busy ? 'Working…' : 'Approve' }}
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="rejectAccessRequest(ar)" :disabled="ar._busy">
                <i class="mdi mdi-close me-1"></i> Reject
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- PENDING -->
      <div v-if="tab==='pending'" class="pc-panel">
        <div v-if="pending.length === 0" class="text-center py-5 text-muted">
          <i class="mdi mdi-check-decagram mdi-48px text-success"></i><br>No pending applications.
        </div>
        <div v-for="o in pending" :key="o.organization_id" class="pc-org-card pending">
          <div class="d-flex flex-wrap align-items-start gap-3">
            <div class="pc-org-avatar"><i :class="typeIcon(o.org_type)"></i></div>
            <div class="flex-grow-1">
              <div class="d-flex flex-wrap align-items-center gap-2">
                <strong>{{ o.org_name }}</strong>
                <span class="badge bg-secondary">{{ o.org_type }}</span>
                <span class="text-muted small">code: {{ o.org_code }}</span>
              </div>
              <div class="small text-muted mt-1">
                <i class="mdi mdi-account me-1"></i>{{ o.primary_contact_name }} ·
                <i class="mdi mdi-email-outline ms-2 me-1"></i>{{ o.primary_contact_email }} ·
                <i class="mdi mdi-phone-outline ms-2 me-1"></i>{{ o.primary_contact_phone }}
              </div>
              <div class="small text-muted mt-1" v-if="o.kra_pin || o.registration_number">
                <span v-if="o.kra_pin">KRA: <code>{{ o.kra_pin }}</code></span>
                <span v-if="o.registration_number" class="ms-3">Reg: <code>{{ o.registration_number }}</code></span>
              </div>
              <div class="small text-muted mt-1">Submitted {{ fmtDate(o.created_at) }} · IP {{ o.signup_ip || '—' }}</div>
            </div>
            <div class="d-flex flex-column gap-2">
              <button class="btn btn-sm btn-success" @click="approve(o)">
                <i class="mdi mdi-check me-1"></i> Approve
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="reject(o)">
                <i class="mdi mdi-close me-1"></i> Reject
              </button>
              <button class="btn btn-sm btn-link" @click="openOrg(o)">Details</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ALL ORGS -->
      <div v-if="tab==='all'" class="pc-panel">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-center">
          <label class="small mb-0">Status:</label>
          <select v-model="filterStatus" class="form-select form-select-sm pc-filter">
            <option value="">All</option><option value="active">Active</option>
            <option value="pending_approval">Pending</option><option value="warning">Warning</option>
            <option value="suspended">Suspended</option><option value="rejected">Rejected</option>
          </select>
          <label class="small mb-0">Type:</label>
          <select v-model="filterType" class="form-select form-select-sm pc-filter">
            <option value="">All</option><option value="county">County</option>
            <option value="ngo">NGO</option><option value="private_group">Private group</option>
            <option value="individual">Individual</option>
          </select>
        </div>

        <div class="table-responsive">
          <table class="table table-hover pc-table">
            <thead><tr>
              <th>Name</th><th>Type</th><th>Status</th><th>Plan</th><th class="text-end">Cycle KES</th>
              <th>Paid through</th><th class="text-end">Hospitals</th><th></th>
            </tr></thead>
            <tbody>
              <tr v-for="o in filteredOrgs" :key="o.organization_id">
                <td>
                  <a href="javascript:void(0)" @click="openOrg(o)"><strong>{{ o.org_name }}</strong></a>
                  <div class="small text-muted">{{ o.org_code }}</div>
                </td>
                <td><span class="badge bg-light text-dark">{{ o.org_type }}</span></td>
                <td><span :class="`badge bg-${statusColor(o.status)}`">{{ o.status }}</span></td>
                <td>{{ o.plan_name || '—' }}</td>
                <td class="text-end">{{ fmt(o.cycle_amount_kes) }}</td>
                <td>{{ o.paid_through_date || '—' }}</td>
                <td class="text-end">{{ o.hospital_count }}</td>
                <td><button class="btn btn-sm btn-outline-primary" @click="openOrg(o)">Open</button></td>
              </tr>
              <tr v-if="filteredOrgs.length === 0"><td colspan="8" class="text-center text-muted py-4">No organisations match.</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- BILLING DASHBOARD -->
      <div v-if="tab==='billing'" class="pc-panel">
        <div class="row g-3">
          <div class="col-md-6">
            <div class="pc-section-h"><i class="mdi mdi-alert-circle text-danger me-1"></i> Overdue ({{ (dash.overdue||[]).length }})</div>
            <div v-if="!dash.overdue || dash.overdue.length === 0" class="text-muted small py-3">None overdue. 🎉</div>
            <div v-for="o in (dash.overdue||[])" :key="'ov'+o.organization_id" class="pc-bill-row">
              <div>
                <strong>{{ o.org_name }}</strong>
                <div class="small text-muted">{{ o.plan_name }} · paid through {{ o.paid_through_date }}</div>
              </div>
              <div class="text-end">
                <div class="text-danger fw-bold">{{ o.days_overdue }}d late</div>
                <div class="small">{{ fmt(o.cycle_amount_kes) }} / {{ o.billing_cycle }}</div>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="pc-section-h"><i class="mdi mdi-calendar-clock text-warning me-1"></i> Upcoming (next 30 days)</div>
            <div v-if="!dash.upcoming_30_days || dash.upcoming_30_days.length === 0" class="text-muted small py-3">Nothing due in the next 30 days.</div>
            <div v-for="o in (dash.upcoming_30_days||[])" :key="'up'+o.organization_id" class="pc-bill-row">
              <div>
                <strong>{{ o.org_name }}</strong>
                <div class="small text-muted">{{ o.plan_name }} · {{ fmt(o.cycle_amount_kes) }} / {{ o.billing_cycle }}</div>
              </div>
              <div class="text-end">
                <div class="fw-bold">{{ o.days_left }}d</div>
                <div class="small text-muted">{{ o.paid_through_date }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="pc-section-h mt-4"><i class="mdi mdi-cash-multiple me-1"></i> All payments</div>
        <div class="table-responsive">
          <table class="table pc-table">
            <thead><tr>
              <th>Date paid</th><th>Org</th><th>Period</th><th class="text-end">Amount</th>
              <th>Method</th><th>Ref</th><th>Recorded by</th><th></th>
            </tr></thead>
            <tbody>
              <tr v-for="p in payments" :key="p.payment_id" :class="{'pc-void': p.is_void==1}">
                <td>{{ p.paid_on }}</td>
                <td>{{ p.org_name }}</td>
                <td class="small">{{ p.period_start }} → {{ p.period_end }}</td>
                <td class="text-end fw-bold">{{ fmt(p.amount_kes) }}</td>
                <td><span class="badge bg-light text-dark">{{ p.payment_method }}</span></td>
                <td><code class="small">{{ p.reference_no || '—' }}</code></td>
                <td class="small">#{{ p.recorded_by }}</td>
                <td>
                  <button v-if="p.is_void != 1" class="btn btn-sm btn-link text-danger" @click="voidPayment(p)">Void</button>
                  <span v-else class="badge bg-secondary">VOID</span>
                </td>
              </tr>
              <tr v-if="payments.length === 0"><td colspan="8" class="text-center text-muted py-4">No payments recorded yet.</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- AUDIT -->
      <div v-if="tab==='audit'" class="pc-panel">
        <div class="d-flex flex-wrap gap-2 mb-3 align-items-end">
          <div><label class="small mb-0">From</label>
            <input v-model="audFilters.date_from" type="date" class="form-control form-control-sm pc-filter">
          </div>
          <div><label class="small mb-0">To</label>
            <input v-model="audFilters.date_to" type="date" class="form-control form-control-sm pc-filter">
          </div>
          <div><label class="small mb-0">Action</label>
            <select v-model="audFilters.action_type" class="form-select form-select-sm pc-filter">
              <option value="">All</option>
              <option v-for="a in auditActions" :key="a" :value="a">{{ a }}</option>
            </select>
          </div>
          <div><label class="small mb-0">Org</label>
            <select v-model="audFilters.target_org_id" class="form-select form-select-sm pc-filter">
              <option value="">All</option>
              <option v-for="o in orgs" :key="o.organization_id" :value="o.organization_id">{{ o.org_name }}</option>
            </select>
          </div>
          <button class="btn btn-sm btn-primary" @click="loadAudit"><i class="mdi mdi-filter me-1"></i>Apply</button>
          <button class="btn btn-sm btn-link" @click="clearAuditFilters">Reset</button>
          <span class="ms-auto small text-muted">{{ auditCount }} of {{ auditTotal }} events</span>
        </div>

        <p class="text-muted small mb-2">Platform-level events only. Per-hospital activity is in each facility's <code>audit_logs</code>.</p>

        <div class="table-responsive">
          <table class="table pc-table">
            <thead><tr><th style="width:140px">When</th><th>Action</th><th>Org</th><th>Description</th><th>Actor</th><th class="d-none d-lg-table-cell">IP</th></tr></thead>
            <tbody>
              <tr v-for="a in audit" :key="a.audit_id">
                <td class="small">{{ fmtDate(a.created_at) }}</td>
                <td><span :class="`badge ${actionBadge(a.action_type)}`">{{ a.action_type }}</span></td>
                <td>
                  <span v-if="a.target_org_name">{{ a.target_org_name }}<br><code class="small text-muted">{{ a.target_org_code }}</code></span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td class="small">{{ a.description }}</td>
                <td class="small">
                  <strong>{{ (a.actor_username || ('#'+a.actor_user_id)) }}</strong><br>
                  <span class="text-muted">{{ a.actor_role }}</span>
                </td>
                <td class="small d-none d-lg-table-cell text-muted"><code>{{ a.ip_address || '—' }}</code></td>
              </tr>
              <tr v-if="audit.length === 0"><td colspan="6" class="text-center text-muted py-4">No audit events match these filters.</td></tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- ORG DETAIL DRAWER -->
    <div v-if="selected" class="pc-drawer-bg" @click.self="selected = null">
      <div class="pc-drawer">
        <div class="d-flex align-items-start justify-content-between mb-3">
          <div>
            <h5 class="mb-1">{{ selected.org_name }}</h5>
            <div class="small text-muted">{{ selected.org_code }} · {{ selected.org_type }} ·
              <span :class="`badge bg-${statusColor(selected.status)}`">{{ selected.status }}</span>
            </div>
          </div>
          <button class="btn-close" @click="selected = null"></button>
        </div>

        <ul class="nav nav-pills nav-fill mb-3 pc-detail-tabs">
          <li class="nav-item"><a class="nav-link" :class="{active:dtab==='profile'}" href="javascript:void(0)" @click="dtab='profile'">Profile</a></li>
          <li class="nav-item"><a class="nav-link" :class="{active:dtab==='subscription'}" href="javascript:void(0)" @click="dtab='subscription'">Subscription</a></li>
          <li class="nav-item"><a class="nav-link" :class="{active:dtab==='payments'}" href="javascript:void(0)" @click="dtab='payments'">Payments</a></li>
          <li class="nav-item"><a class="nav-link" :class="{active:dtab==='features'}" href="javascript:void(0)" @click="dtab='features'">Features</a></li>
        </ul>

        <div v-if="dtab==='profile'">
          <div class="row g-2 small">
            <div class="col-6"><b>Legal name:</b> {{ selected.legal_name || '—' }}</div>
            <div class="col-6"><b>KRA:</b> {{ selected.kra_pin || '—' }}</div>
            <div class="col-6"><b>Registration:</b> {{ selected.registration_number || '—' }}</div>
            <div class="col-6"><b>License:</b> {{ selected.license_number || '—' }}</div>
            <div class="col-6"><b>Contact:</b> {{ selected.primary_contact_name }}</div>
            <div class="col-6"><b>Email:</b> {{ selected.primary_contact_email }}</div>
            <div class="col-6"><b>Phone:</b> {{ selected.primary_contact_phone }}</div>
            <div class="col-6"><b>Submitted:</b> {{ fmtDate(selected.created_at) }}</div>
            <div class="col-12"><b>Address:</b> {{ selected.address || '—' }}</div>
          </div>
          <hr>
          <div class="d-flex gap-2 flex-wrap">
            <button v-if="selected.status==='pending_approval'" class="btn btn-sm btn-success" @click="approve(selected)"><i class="mdi mdi-check me-1"></i>Approve</button>
            <button v-if="selected.status==='pending_approval'" class="btn btn-sm btn-outline-danger" @click="reject(selected)"><i class="mdi mdi-close me-1"></i>Reject</button>
            <button v-if="selected.status==='active' || selected.status==='warning'" class="btn btn-sm btn-warning" @click="suspend(selected)"><i class="mdi mdi-pause me-1"></i>Suspend</button>
            <button v-if="selected.status==='suspended'" class="btn btn-sm btn-success" @click="reactivate(selected)"><i class="mdi mdi-play me-1"></i>Reactivate</button>
          </div>
        </div>

        <div v-if="dtab==='subscription'">
          <div class="row g-2 mb-3">
            <div class="col-md-6"><label class="small">Plan name</label><input v-model="subEdit.plan_name" class="form-control form-control-sm"></div>
            <div class="col-md-3"><label class="small">Cycle</label>
              <select v-model="subEdit.billing_cycle" class="form-select form-select-sm">
                <option>monthly</option><option>quarterly</option><option>annual</option><option>one_off</option><option>free</option>
              </select>
            </div>
            <div class="col-md-3"><label class="small">Amount (KES)</label><input v-model="subEdit.cycle_amount_kes" type="number" class="form-control form-control-sm"></div>
            <div class="col-md-3"><label class="small">Status</label>
              <select v-model="subEdit.status" class="form-select form-select-sm">
                <option>trial</option><option>active</option><option>warning</option><option>suspended</option><option>cancelled</option>
              </select>
            </div>
            <div class="col-md-3"><label class="small">Paid through</label><input :value="(detail.subscription||{}).paid_through_date || '—'" disabled class="form-control form-control-sm"></div>
            <div class="col-md-3"><label class="small">Auto-suspend after</label><input v-model="subEdit.auto_suspend_on_overdue_days" type="number" class="form-control form-control-sm"></div>
            <div class="col-12"><label class="small">Notes</label><textarea v-model="subEdit.notes" rows="2" class="form-control form-control-sm"></textarea></div>
          </div>
          <button class="btn btn-sm btn-primary" @click="saveSubscription"><i class="mdi mdi-content-save me-1"></i>Save subscription</button>
        </div>

        <div v-if="dtab==='payments'">
          <div class="pc-section-h">Record a payment</div>
          <div class="row g-2 mb-3">
            <div class="col-md-4"><label class="small">Period start</label><input v-model="payForm.period_start" type="date" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small">Period end</label><input v-model="payForm.period_end" type="date" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small">Paid on</label><input v-model="payForm.paid_on" type="date" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small">Amount (KES)</label><input v-model="payForm.amount_kes" type="number" class="form-control form-control-sm"></div>
            <div class="col-md-4"><label class="small">Method</label>
              <select v-model="payForm.payment_method" class="form-select form-select-sm">
                <option>mpesa</option><option>bank_transfer</option><option>cash</option><option>lpo</option><option>cheque</option><option>waiver</option><option>other</option>
              </select>
            </div>
            <div class="col-md-4"><label class="small">Reference</label><input v-model="payForm.reference_no" class="form-control form-control-sm" placeholder="M-Pesa code / LPO no."></div>
            <div class="col-12"><label class="small">Notes</label><input v-model="payForm.notes" class="form-control form-control-sm"></div>
          </div>
          <button class="btn btn-sm btn-success" @click="recordPayment"><i class="mdi mdi-cash-plus me-1"></i>Record payment</button>

          <div class="pc-section-h mt-4">Payment history</div>
          <table class="table table-sm pc-table">
            <thead><tr><th>Date</th><th>Period</th><th class="text-end">Amount</th><th>Method</th><th>Ref</th><th></th></tr></thead>
            <tbody>
              <tr v-for="p in (detail.payments||[])" :key="p.payment_id" :class="{'pc-void': p.is_void==1}">
                <td>{{ p.paid_on }}</td>
                <td class="small">{{ p.period_start }} → {{ p.period_end }}</td>
                <td class="text-end">{{ fmt(p.amount_kes) }}</td>
                <td>{{ p.payment_method }}</td>
                <td><code class="small">{{ p.reference_no }}</code></td>
                <td><button v-if="p.is_void != 1" class="btn btn-sm btn-link text-danger" @click="voidPayment(p)">Void</button>
                    <span v-else class="badge bg-secondary">void</span></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="dtab==='features'">
          <p class="small text-muted">Defaults are ON. Use the toggle to override per-org. Disabled features will be blocked in handlers (helper coming next).</p>
          <div class="row g-2">
            <div class="col-md-6" v-for="f in features" :key="f.feature_code">
              <div class="pc-feature-row">
                <div>
                  <strong class="small">{{ f.feature_name }}</strong>
                  <div class="text-muted small">{{ f.feature_code }} <span v-if="f.source==='override'" class="badge bg-warning text-dark ms-1">override</span></div>
                </div>
                <div class="form-check form-switch">
                  <input class="form-check-input" type="checkbox" :checked="f.is_enabled==1" @change="toggleFeature(f, $event)">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { hms } from "@/api";
import Navigation from "@/components/hms-auth/Navigation.vue";
import brand from "@/brand";

const auth = () => ({ headers: { Authorization: "Bearer " + localStorage.getItem("authToken") }});

export default {
  name: "PlatformConsole",
  components: { Navigation },
  data() {
    return {
      brand,
      tab: "pending", dtab: "profile",
      pending: [], orgs: [], payments: [], audit: [], dash: {},
      accessRequests: [], accessStatusFilter: "pending", accessRequestsPending: 0,
      auditActions: [], auditCount: 0, auditTotal: 0,
      audFilters: { date_from: "", date_to: "", action_type: "", target_org_id: "" },
      filterStatus: "", filterType: "",
      selected: null, detail: {}, features: [],
      subEdit: { plan_name: "", billing_cycle: "monthly", cycle_amount_kes: 0, status: "active", notes: "", auto_suspend_on_overdue_days: 30 },
      payForm: { period_start: "", period_end: "", paid_on: "", amount_kes: 0, payment_method: "mpesa", reference_no: "", notes: "" },
      newOrg: {
        show: false, busy: false, error: "",
        form: {
          hospital_name: "", city: "", hospital_email: "", hospital_phone: "",
          first_name: "", last_name: "", username: "", email: "", phone: "", password: ""
        }
      },
    };
  },
  computed: {
    filteredOrgs() {
      return this.orgs.filter(o =>
        (!this.filterStatus || o.status === this.filterStatus) &&
        (!this.filterType   || o.org_type === this.filterType));
    }
  },
  watch: {
    tab(newVal) {
      if (newVal === "audit" && this.audit.length === 0) this.loadAudit();
    }
  },
  mounted() {
    const u = JSON.parse(localStorage.getItem("userProfile") || "{}");
    if (!["platform_admin","craft_care_support"].includes(u.role_code)) {
      this.$router.replace("/login"); return;
    }
    // Honour ?tab= from the URL (used by the nav bell to jump to access requests)
    const q = this.$route && this.$route.query && this.$route.query.tab;
    if (q === "access" || q === "billing" || q === "audit" || q === "all") {
      this.tab = q;
    }
    this.refreshAll();
  },
  methods: {
    openNewOrg() {
      this.newOrg.form = { hospital_name: "", city: "", hospital_email: "", hospital_phone: "",
        first_name: "", last_name: "", username: "", email: "", phone: "", password: "" };
      this.newOrg.error = "";
      this.newOrg.show = true;
    },
    closeNewOrg() { this.newOrg.show = false; },
    async submitNewOrg() {
      const f = this.newOrg.form;
      this.newOrg.error = "";
      if (!f.hospital_name || !f.first_name || !f.last_name || !f.username || !f.email || !f.password) {
        this.newOrg.error = "Hospital name, admin name, username, email and password are required."; return;
      }
      if (f.password.length < 8) { this.newOrg.error = "Password must be at least 8 characters."; return; }
      if (!/^[A-Za-z0-9._]+$/.test(f.username)) { this.newOrg.error = "Username: letters, numbers, dots, underscores only."; return; }

      this.newOrg.busy = true;
      try {
        // Step 1 — registerHospital (public endpoint; creates org in pending_approval).
        const signup = await this.post("registerHospital", f);
        const orgId = signup && signup.organization_id;
        if (!orgId) throw new Error("Registration succeeded but no organization_id returned.");
        // Step 2 — auto-approve as the platform admin doing manual onboarding.
        await this.post("approveOrganization", { organization_id: orgId, notes: "Manually onboarded via Platform Console." });
        this.newOrg.show = false;
        this.newOrg.busy = false;
        alert("Onboarded and approved. Login credentials emailed to " + f.email + ".");
        await this.refreshAll();
      } catch (e) {
        this.newOrg.busy = false;
        this.newOrg.error = e.message || "Onboarding failed.";
      }
    },

    fmt(n) { return n == null ? "—" : Number(n).toLocaleString("en-KE", { maximumFractionDigits: 0 }); },
    fmtDate(s) { return s ? String(s).replace("T"," ").substring(0,16) : "—"; },
    statusColor(s) { return ({active:"success",pending_approval:"warning",warning:"warning",suspended:"danger",rejected:"secondary"})[s] || "secondary"; },
    typeIcon(t) { return ({county:"mdi mdi-domain",ngo:"mdi mdi-account-heart",private_group:"mdi mdi-hospital-building",individual:"mdi mdi-account-circle"})[t] || "mdi mdi-domain"; },
    async post(fn, body) {
      const p = new URLSearchParams(); p.append("function", fn);
      Object.entries(body || {}).forEach(([k,v]) => p.append(k, v));
      const r = await axios.post(hms + "?function=" + fn, p, auth());
      if (!r.data || !r.data.success) throw new Error((r.data && r.data.message) || "Request failed");
      return r.data.data;
    },
    async refreshAll() {
      try {
        this.dash      = await this.post("getBillingDashboard");
        this.pending   = (await this.post("listPendingOrganizations")).rows || [];
        this.orgs      = (await this.post("listOrganizations")).rows || [];
        this.payments  = (await this.post("listSubscriptionPayments")).rows || [];
      } catch (e) { alert("Load failed: " + e.message); }
      if (this.tab === "audit")  this.loadAudit();
      if (this.tab === "access") this.loadAccessRequests();
      this.loadAccessRequestCount();
    },
    async loadAccessRequestCount() {
      try {
        const d = await this.post("getAccessRequestCount");
        this.accessRequestsPending = (d && d.count) || 0;
      } catch (_) { /* silent */ }
    },
    async loadAccessRequests() {
      try {
        const d = await this.post("listAccessRequests", { status: this.accessStatusFilter });
        const rows = (d && d.Requests) || [];
        // Seed the per-row grant level with the requested level so approve is one click.
        this.accessRequests = rows.map(r => ({ ...r, _grantLevel: r.required_level || 'view' }));
      } catch (e) { alert("Load access requests failed: " + e.message); }
    },
    switchToAccess() {
      this.tab = "access";
      this.loadAccessRequests();
    },
    async approveAccessRequest(ar) {
      if (ar._busy) return;
      ar._busy = true;
      try {
        await this.post("decideAccessRequest", {
          request_id:    ar.request_id,
          decision:      "approve",
          granted_level: ar._grantLevel || ar.required_level || "view"
        });
      } catch (e) {
        // "already decided" is idempotent — treat as success and refresh silently
        if (!/already decided|not found/i.test(e.message)) {
          alert("Approve failed: " + e.message);
        }
      } finally {
        await this.loadAccessRequests();
        await this.loadAccessRequestCount();
      }
    },
    async rejectAccessRequest(ar) {
      if (ar._busy) return;
      const note = prompt("Reason for rejection (optional):", "") || "";
      ar._busy = true;
      try {
        await this.post("decideAccessRequest", {
          request_id: ar.request_id,
          decision:   "reject",
          note
        });
      } catch (e) {
        if (!/already decided|not found/i.test(e.message)) {
          alert("Reject failed: " + e.message);
        }
      } finally {
        await this.loadAccessRequests();
        await this.loadAccessRequestCount();
      }
    },
    actionBadge(a) {
      if (!a) return "bg-secondary";
      if (a.endsWith("_received") || a === "org_approved")   return "bg-success";
      if (a === "org_rejected" || a === "org_suspended")      return "bg-danger";
      if (a === "org_reactivated")                            return "bg-info";
      if (a === "payment_recorded")                           return "bg-success";
      if (a === "payment_voided")                             return "bg-warning text-dark";
      if (a === "feature_toggled")                            return "bg-info";
      if (a === "subscription_updated" || a === "org_profile_updated") return "bg-primary";
      if (a === "hospital_created_under_org" || a === "hospital_admin_invited") return "bg-primary";
      return "bg-secondary";
    },
    clearAuditFilters() {
      this.audFilters = { date_from: "", date_to: "", action_type: "", target_org_id: "" };
      this.loadAudit();
    },
    async loadAudit() {
      try {
        const body = {};
        if (this.audFilters.date_from)     body.date_from     = this.audFilters.date_from;
        if (this.audFilters.date_to)       body.date_to       = this.audFilters.date_to;
        if (this.audFilters.action_type)   body.action_type   = this.audFilters.action_type;
        if (this.audFilters.target_org_id) body.target_org_id = this.audFilters.target_org_id;
        body.limit = 200;
        const r = await this.post("listPlatformAuditLog", body);
        this.audit         = r.rows || [];
        this.auditCount    = r.count || 0;
        this.auditTotal    = r.total_matching || 0;
        this.auditActions  = r.distinct_actions || [];
      } catch (e) {
        alert("Audit load failed: " + e.message);
      }
    },
    async openOrg(o) {
      try {
        const d = await this.post("getOrganization", { organization_id: o.organization_id });
        this.selected = d.organization;
        this.detail   = d;
        const sub = d.subscription || {};
        this.subEdit = {
          plan_name: sub.plan_name || "",
          billing_cycle: sub.billing_cycle || "monthly",
          cycle_amount_kes: sub.cycle_amount_kes || 0,
          status: sub.status || "active",
          notes: sub.notes || "",
          auto_suspend_on_overdue_days: sub.auto_suspend_on_overdue_days || 30
        };
        const today = new Date().toISOString().substring(0,10);
        this.payForm.paid_on = today;
        this.payForm.amount_kes = sub.cycle_amount_kes || 0;
        // Load payments + features for the drawer
        this.detail.payments = (await this.post("listSubscriptionPayments", { organization_id: o.organization_id, include_void: 1 })).rows || [];
        this.features = (await this.post("listOrgFeatureFlags", { organization_id: o.organization_id })).features || [];
        this.dtab = "profile";
      } catch (e) { alert("Open failed: " + e.message); }
    },
    async approve(o) {
      const notes = prompt("Approval notes (optional):", ""); if (notes === null) return;
      try { await this.post("approveOrganization", { organization_id: o.organization_id, notes }); await this.refreshAll(); if (this.selected) this.selected = null; }
      catch (e) { alert(e.message); }
    },
    async reject(o) {
      const reason = prompt("Reason for rejection (required):", ""); if (!reason) return;
      try { await this.post("rejectOrganization", { organization_id: o.organization_id, rejection_reason: reason }); await this.refreshAll(); if (this.selected) this.selected = null; }
      catch (e) { alert(e.message); }
    },
    async suspend(o) {
      const reason = prompt("Suspension reason:", "Non-payment"); if (!reason) return;
      try { await this.post("suspendOrganization", { organization_id: o.organization_id, suspension_reason: reason }); await this.refreshAll(); if (this.selected) await this.openOrg(o); }
      catch (e) { alert(e.message); }
    },
    async reactivate(o) {
      try { await this.post("reactivateOrganization", { organization_id: o.organization_id }); await this.refreshAll(); if (this.selected) await this.openOrg(o); }
      catch (e) { alert(e.message); }
    },
    async saveSubscription() {
      try { await this.post("updateOrgSubscription", { organization_id: this.selected.organization_id, ...this.subEdit }); await this.refreshAll(); await this.openOrg(this.selected); alert("Subscription saved."); }
      catch (e) { alert(e.message); }
    },
    async recordPayment() {
      const f = this.payForm;
      if (!f.period_start || !f.period_end || !f.amount_kes) { alert("Period start/end and amount are required."); return; }
      try { await this.post("recordSubscriptionPayment", { organization_id: this.selected.organization_id, ...f }); await this.refreshAll(); await this.openOrg(this.selected); }
      catch (e) { alert(e.message); }
    },
    async voidPayment(p) {
      const reason = prompt("Reason for voiding:", ""); if (!reason) return;
      try { await this.post("voidSubscriptionPayment", { payment_id: p.payment_id, void_reason: reason }); await this.refreshAll(); if (this.selected) await this.openOrg(this.selected); }
      catch (e) { alert(e.message); }
    },
    async toggleFeature(f, evt) {
      const enabled = evt.target.checked ? 1 : 0;
      let reason = "";
      if (!enabled) { reason = prompt("Reason for disabling " + f.feature_name + ":", "Not in plan") || ""; }
      try { await this.post("toggleOrgFeatureFlag", { organization_id: this.selected.organization_id, feature_code: f.feature_code, is_enabled: enabled, disabled_reason: reason }); this.features = (await this.post("listOrgFeatureFlags", { organization_id: this.selected.organization_id })).features || []; }
      catch (e) { alert(e.message); evt.target.checked = !enabled; }
    },
  }
};
</script>

<style scoped>
.pc-page { background: var(--hms-bg2); min-height: 100vh; padding-top: 70px; }
.pc-system-owner-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  background: linear-gradient(135deg, #F5821F 0%, #d96612 100%);
  color: #fff;
  box-shadow: 0 2px 6px rgba(245,130,31,0.35);
  border: 1px solid rgba(0,0,0,0.05);
  white-space: nowrap;
}
.pc-kpi { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 12px; padding: 16px; height: 100%; }
.pc-kpi-label { font-size: 11px; text-transform: uppercase; color: var(--hms-s); letter-spacing: .5px; }
.pc-kpi-val { font-size: 28px; font-weight: 800; color: var(--hms-h); margin: 4px 0 2px; }
.pc-kpi-sub { font-size: 11px; color: var(--hms-s); }

.pc-tabs { border-bottom: 2px solid var(--hms-br); }
.pc-tabs .nav-link { color: var(--hms-s); border: none; border-bottom: 3px solid transparent; padding: 12px 18px; font-weight: 600; }
.pc-tabs .nav-link.active { color: var(--hms-primary); background: transparent; border-bottom-color: var(--hms-primary); }

.pc-panel { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-top: none; padding: 20px; border-radius: 0 0 12px 12px; }

.pc-org-card { background: var(--hms-bg2); border: 1px solid var(--hms-br); border-radius: 10px; padding: 14px; margin-bottom: 10px; }
.pc-org-card.pending { border-left: 4px solid var(--hms-warning, #f59e0b); }
.pc-org-avatar { width: 44px; height: 44px; border-radius: 50%; background: var(--hms-primary-lt, rgba(59,130,246,.1)); color: var(--hms-primary); display: flex; align-items: center; justify-content: center; font-size: 22px; }

.pc-filter { width: auto; }
.pc-table th { font-size: 12px; text-transform: uppercase; color: var(--hms-s); font-weight: 700; letter-spacing: .3px; }
.pc-table .pc-void { opacity: .45; text-decoration: line-through; }

.pc-section-h { font-size: 12px; text-transform: uppercase; color: var(--hms-s); font-weight: 700; letter-spacing: .5px; margin: 16px 0 8px; }

.pc-bill-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; border: 1px solid var(--hms-br); border-radius: 8px; margin-bottom: 8px; background: var(--hms-bg2); }

/* Drawer */
.pc-drawer-bg { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1050; display: flex; justify-content: flex-end; }
.pc-drawer { background: var(--hms-bg1); width: min(640px, 100vw); height: 100vh; overflow-y: auto; padding: 20px; border-left: 1px solid var(--hms-br); }
.pc-detail-tabs .nav-link { font-size: 12px; padding: 6px 10px; color: var(--hms-s); }
.pc-detail-tabs .nav-link.active { background: var(--hms-primary); color: #fff; }

.pc-feature-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 12px; border: 1px solid var(--hms-br); border-radius: 8px; background: var(--hms-bg2); }

label.small { font-size: 11px; font-weight: 600; color: var(--hms-h); margin-bottom: 2px; }

/* New Organisation modal */
.pc-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,0.55); z-index: 9000; display: flex; align-items: flex-start; justify-content: center; padding: 40px 20px; overflow-y: auto; }
.pc-modal { background: var(--hms-bg1, #fff); border-radius: 12px; width: min(720px, 100%); box-shadow: 0 24px 60px rgba(0,0,0,.35); overflow: hidden; }
.pc-modal-head { padding: 14px 20px; border-bottom: 1px solid var(--hms-br); display: flex; align-items: center; justify-content: space-between; font-size: 15px; color: var(--hms-h); }
.pc-modal-body { padding: 18px 20px; max-height: 70vh; overflow-y: auto; }
.pc-modal-foot { padding: 12px 20px; border-top: 1px solid var(--hms-br); display: flex; justify-content: flex-end; gap: 8px; background: var(--hms-bg2); }
.pc-modal-section-h { font-size: 11px; text-transform: uppercase; color: var(--hms-s); font-weight: 700; letter-spacing: .4px; margin: 12px 0 8px; padding-bottom: 4px; border-bottom: 1px solid var(--hms-br); }
</style>
